import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores/FlashesStore';
import { initStore } from '@/stores/store-utils';
import {
  FAVORITE_TYPES,
  FavoriteType,
  UserFavorite,
} from '@/types/user_favorites';

export const useUserFavoritesStore = defineStore('UserFavorites', () => {
  const favorites = ref<UserFavorite[]>([]);
  const flashesStore = useFlashesStore();

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const { data } = await crossbeamApi.GET('/v0.1/users/favorites');
      if (data) favorites.value = data;
    },
  );

  function getFavoriteByResourceUuid(resourceUuid: string) {
    return favorites.value.find(
      ({ resource_uuid: uuid }) => uuid === resourceUuid,
    );
  }

  async function addFavorite({
    favoriteType,
    resourceUuid,
  }: {
    favoriteType: FavoriteType;
    resourceUuid: string;
  }) {
    const payload = {
      favorite_type: favoriteType,
      resource_uuid: resourceUuid,
    };
    const { data, error } = await crossbeamApi.POST('/v0.1/users/favorites', {
      body: payload,
    });

    if (error) {
      captureException(error);
      flashesStore.addUnhandledError(error);
      return;
    }

    favorites.value = [...favorites.value, data];
  }

  async function removeFavorite({ id }: { id: string }) {
    const { error } = await crossbeamApi.DELETE(
      '/v0.1/users/favorites/{uuid}',
      {
        params: { path: { uuid: id } },
      },
    );

    if (error) {
      captureException(error);
      flashesStore.addUnhandledError(error);
      return;
    }

    favorites.value = favorites.value.filter(
      ({ id: favoriteId }) => favoriteId !== id,
    );
  }

  const partnerFavorites = computed(() =>
    favorites.value.filter(
      ({ favorite_type: favoriteType }) =>
        favoriteType === FAVORITE_TYPES.PARTNER,
    ),
  );

  refresh({ useCache: true });

  return {
    favorites,
    error: initError,
    ready,
    readySync,
    running,
    partnerFavorites,
    addFavorite,
    removeFavorite,
    refreshUserFavoritesStore: refresh,
    getFavoriteByResourceUuid,
  };
});
